import { t as __t } from "i18next";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import CopyrightFooter from "../../../components/footer/employers/CopyrightFooter";
import DashboardHeader from "../../../components/header/DashboardHeader";
import DashboardEmployerSidebar from "../../../components/header/DashboardSidebar";
import MobileMenu from "../../../components/header/MobileMenu";
import {
  callCreateManualPaymentCase,
  callDecisionManualPaymentCase,
  callFinalizeManualPaymentCase,
  callGetManualPaymentCase,
  callGetManualPaymentCases,
  callGetManualPaymentProof,
  callPreflightManualPaymentCase,
  callSearchPendingPayment,
  callSubmitManualPaymentProof,
  callSyncManualPaymentCase,
} from "../../../api/manual-payment/service";

import "./manual-payment.css";
import DatePicker from "react-datepicker";
import { dateToString } from "../../../utils/commonUtil";
import moment from "moment";
import { DocumentPreviewTrigger, openDocumentPreview } from "../../../components/document-preview";

const STATUS_KEYS = [
  "OPEN",
  "SUBMITTED",
  "MANUAL_VERIFIED",
  "REJECTED",
  "ALREADY_POSTED",
];

const FLOW_OPTIONS = [
  "VEHICLE_NEW",
  "LIFE_NEW",
  "LIFE_MONTHLY",
  "LIFE_BATCH",
  "VEHICLE_DEBT",
  "UNCLASSIFIED",
];

const getStatusLabel = (t, status) =>
  t(`manualPayment.status.${status}`, {
    defaultValue: status || "—",
  });

const getFlowLabel = (t, flowType) =>
  t(`manualPayment.flow.${flowType}`, {
    defaultValue: flowType || "—",
  });

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB");
};

const toLaosISOString = (value) => {
  if (!value) return "";

  const normalizedValue = value.length === 16 ? `${value}:00` : value;
  return new Date(`${normalizedValue}+01:00`).toISOString();

};

const readError = (error) =>
  error?.response?.data?.responseDescription ||
  error?.message ||
  __t("manualPayment.messages.systemError");

const isSuccess = (response) => response?.responseCode === "00000";

const getFinalizationSourceLabel = (source) => {
  if (source === "MANUAL") return __t("manualPayment.source.MANUAL");
  if (source === "PUBNUB") return __t("manualPayment.source.PUBNUB");
  return __t("manualPayment.source.DATABASE");
};

const AlertBox = ({ message, tone = "info" }) => {
  if (!message) return null;

  return (
    <div role="alert" className={`mpr-alert mpr-alert-${tone}`}>
      {message}
    </div>
  );
};

const getDispatchResponse = async (dispatch, action) => {
  const result = await dispatch(action);

  if (result?.meta?.requestStatus === "rejected") {
    throw new Error(
      result?.error?.message || __t("manualPayment.messages.apiRequestFailed")
    );
  }

  return result?.payload;
};

const ManualPaymentDesk = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("auto");
  const [flowType, setFlowType] = useState("");
  const [recentAge, setRecentAge] = useState("60");
  const [recentMode, setRecentMode] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [paging, setPaging] = useState({
    totalRecords: 0,
    totalPages: 1,
  });
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState("");
  const [form, setForm] = useState({
    paymentReference: "",
    paidAmount: "",
    paidAt: "",
    note: "",
    slip: null,
  });
  const [decisionNote, setDecisionNote] = useState("");
  const [preflight, setPreflight] = useState(null);
  const [finalNote, setFinalNote] = useState("");
  const [confirmFunds, setConfirmFunds] = useState(false);
  const [proofUrl, setProofUrl] = useState("");
  const [proofMime, setProofMime] = useState("");

  const loadCases = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callGetManualPaymentCases({
          page,
          status,
          q: search,
          flowType,
        })
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      setItems(response.data?.result || []);
      setPaging({
        totalRecords: response.data?.totalRecords || 0,
        totalPages: response.data?.totalPages || 1,
      });
      setWarning("");
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setLoading(false);
    }
  }, [dispatch, page, status, search, flowType]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  useEffect(() => {
    return () => {
      if (proofUrl) {
        URL.revokeObjectURL(proofUrl);
      }
    };
  }, [proofUrl]);

  const closeProof = () => {
    if (proofUrl) {
      URL.revokeObjectURL(proofUrl);
    }
    setProofUrl("");
    setProofMime("");
  };

  const resetActiveState = () => {
    closeProof();
    setActive(null);
    setPreflight(null);
    setFinalNote("");
    setConfirmFunds(false);
    setDecisionNote("");
  };

  const chooseCase = async (id) => {
    setWarning("");
    resetActiveState();

    try {
      const response = await getDispatchResponse(
        dispatch,
        callGetManualPaymentCase(id)
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      setActive(response.data);
      setForm({
        paymentReference: response.data.paymentReference || "",
        paidAmount: String(response.data.expectedAmount || ""),
        paidAt: "",
        note: "",
        slip: null,
      });

      if (response.data.status === "MANUAL_VERIFIED") {
        const preflightResponse = await getDispatchResponse(
          dispatch,
          callPreflightManualPaymentCase(id)
        );
        if (isSuccess(preflightResponse)) {
          setPreflight(preflightResponse.data);
        }
      }
    } catch (error) {
      setWarning(readError(error));
    }
  };

  const handleLookup = async (event) => {
    event.preventDefault();

    if (query.trim().length < 3) {
      setWarning(t("manualPayment.messages.minimumQuery"));
      return;
    }

    setBusy(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callSearchPendingPayment({
          q: query.trim(),
          mode,
          flowType,
        })
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      setCandidates(response.data || []);
      setRecentMode(false);
      setWarning("");
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const findOldPending = async () => {
    setBusy(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callSearchPendingPayment({
          q: "",
          recent: 1,
          minAgeMinutes: recentAge,
          flowType,
        })
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      setCandidates(response.data || []);
      setRecentMode(true);
      setWarning("");
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const openCandidate = async (item) => {
    setBusy(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callCreateManualPaymentCase({
          sourceType: item.sourceType,
          billNumber: item.billNumber,
        })
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      await loadCases();
      await chooseCase(response.data.id);
      setCandidates([]);
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitProof = async (event) => {
    event.preventDefault();

    if (!form.slip || form.slip.size > 5 * 1024 * 1024) {
      setWarning(t("manualPayment.messages.invalidSlip"));
      return;
    }

    const formData = new FormData();
    formData.append("slip", form.slip);
    formData.append("paymentReference", form.paymentReference || "");
    formData.append("paidAmount", form.paidAmount || "");
    formData.append("note", form.note || "");

    formData.append("paidAt", toLaosISOString(form.paidAt));
    setBusy(true);
    try {
      const response = await getDispatchResponse(
        dispatch,
        callSubmitManualPaymentProof({
          id: active.id,
          formData,
        })
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      await chooseCase(active.id);
      await loadCases();

      Swal.fire(
        t("manualPayment.messages.proofSubmittedTitle"),
        t("manualPayment.messages.proofSubmittedText"),
        "success"
      );
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const reviewCase = async (decision) => {
    if (decisionNote.trim().length < 8) {
      setWarning(t("manualPayment.messages.reviewNoteMin"));
      return;
    }

    const title =
      decision === "approve"
        ? t("manualPayment.messages.approveConfirm")
        : t("manualPayment.messages.rejectConfirm");

    const confirmation = await Swal.fire({
      title,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("manualPayment.messages.confirm"),
      cancelButtonText: t("manualPayment.messages.cancel"),
    });

    if (!confirmation.isConfirmed) return;

    setBusy(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callDecisionManualPaymentCase({
          id: active.id,
          body: {
            decision,
            note: decisionNote.trim(),
          },
        })
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      await chooseCase(active.id);
      await loadCases();
      Swal.fire(t("manualPayment.messages.reviewSaved"), "", "success");
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const syncPosted = async () => {
    setBusy(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callSyncManualPaymentCase(active.id)
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      await chooseCase(active.id);
      await loadCases();

      Swal.fire(
        t("manualPayment.messages.syncTitle"),
        response.data.posted
          ? t("manualPayment.messages.syncFound")
          : t("manualPayment.messages.syncNotFound"),
        "info"
      );
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const reloadPreflight = async () => {
    if (!active) return;

    setBusy(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callPreflightManualPaymentCase(active.id)
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      setPreflight(response.data);
      setWarning("");
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const bookPayment = async () => {
    if (!preflight?.ready) {
      setWarning(t("manualPayment.messages.preflightRequired"));
      return;
    }

    if (!confirmFunds || finalNote.trim().length < 12) {
      setWarning(t("manualPayment.messages.finalNoteRequired"));
      return;
    }

    const billNumber = String(active.billNumber).replace(/[<>&"']/g, "");
    const confirmation = await Swal.fire({
      title: t("manualPayment.messages.finalConfirmTitle"),
      html: t("manualPayment.messages.finalConfirmHtml", { billNumber }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("manualPayment.messages.finalConfirmButton"),
      cancelButtonText: t("manualPayment.messages.cancel"),
      confirmButtonColor: "#087a52",
    });

    if (!confirmation.isConfirmed) return;

    setBusy(true);

    try {
      const response = await getDispatchResponse(
        dispatch,
        callFinalizeManualPaymentCase({
          id: active.id,
          body: {
            confirmedIndependent: true,
            note: finalNote.trim(),
          },
        })
      );

      if (!isSuccess(response)) {
        throw new Error(response?.responseDescription);
      }

      await chooseCase(active.id);
      await loadCases();

      Swal.fire(
        t("manualPayment.messages.finalSuccessTitle"),
        t("manualPayment.messages.finalSuccessText"),
        "success"
      );
    } catch (error) {
      setWarning(readError(error));
      await reloadPreflight();
    } finally {
      setBusy(false);
    }
  };

  const showProof = async () => {
    setBusy(true);

    try {
      const blob = await callGetManualPaymentProof(active.id);

      if (proofUrl) {
        URL.revokeObjectURL(proofUrl);
      }
      openDocumentPreview(URL.createObjectURL(blob))
      // setProofMime(blob.type);
      // setProofUrl(URL.createObjectURL(blob));
    } catch (error) {
      setWarning(readError(error));
    } finally {
      setBusy(false);
    }
  };

  const pendingReviewCount = items.filter(
    (item) => item.status === "SUBMITTED"
  ).length;
  const waitingPostCount = items.filter(
    (item) => item.status === "MANUAL_VERIFIED"
  ).length;
  const postedCount = items.filter(
    (item) => item.status === "ALREADY_POSTED"
  ).length;

  return (
    <div className="page-wrapper dashboard">
      <span className="header-span" />
      <DashboardHeader />
      <MobileMenu />
      <DashboardEmployerSidebar />

      <section className="user-dashboard">
        <div className="dashboard-outer mpr">
          <div className="upper-title-box mpr-title-box">
            <div>
              <span className="mpr-eyebrow">
                {t("manualPayment.eyebrow")}
              </span>
              <h4>{t("manualPayment.title")}</h4>
              <p className="mpr-title-description">
                {t("manualPayment.description")}
              </p>
            </div>

            <div className="group-button-header">
              <button
                type="button"
                className="btn btn-outline-success"
                disabled={busy || loading}
                onClick={loadCases}
              >
                <i className="fas fa-sync-alt" />&nbsp; {t("manualPayment.reload")}
              </button>
            </div>
          </div>

          <div
            className="mpr-workflow"
            aria-label={t("manualPayment.workflow.ariaLabel")}
          >
            <div>
              <i>01</i>
              <strong>{t("manualPayment.workflow.step1Title")}</strong>
              <small>{t("manualPayment.workflow.step1Description")}</small>
            </div>
            <span aria-hidden="true">→</span>
            <div>
              <i>02</i>
              <strong>{t("manualPayment.workflow.step2Title")}</strong>
              <small>{t("manualPayment.workflow.step2Description")}</small>
            </div>
            <span aria-hidden="true">→</span>
            <div>
              <i>03</i>
              <strong>{t("manualPayment.workflow.step3Title")}</strong>
              <small>{t("manualPayment.workflow.step3Description")}</small>
            </div>
            <span aria-hidden="true">→</span>
            <div>
              <i>04</i>
              <strong>{t("manualPayment.workflow.step4Title")}</strong>
              <small>{t("manualPayment.workflow.step4Description")}</small>
            </div>
          </div>

          <div className="mpr-kpis">
            <div>
              <small>{t("manualPayment.kpi.currentPage")}</small>
              <strong>{items.length}</strong>
            </div>
            <div>
              <small>{t("manualPayment.kpi.pendingReview")}</small>
              <strong>{pendingReviewCount}</strong>
            </div>
            <div>
              <small>{t("manualPayment.kpi.verifiedWaitingPost")}</small>
              <strong>{waitingPostCount}</strong>
            </div>
            <div>
              <small>{t("manualPayment.kpi.posted")}</small>
              <strong>{postedCount}</strong>
            </div>
          </div>

          <div className="mpr-flowcards">
            <button
              type="button"
              className={!flowType ? "mpr-flowcard selected" : "mpr-flowcard"}
              onClick={() => {
                setFlowType("");
                setPage(1);
              }}
            >
              <span>{t("manualPayment.allCode")}</span>
              <strong>{t("manualPayment.all")}</strong>
            </button>

            {FLOW_OPTIONS.filter((key) => key !== "UNCLASSIFIED").map(
              (key) => (
                <button
                  key={key}
                  type="button"
                  className={
                    flowType === key
                      ? "mpr-flowcard selected"
                      : "mpr-flowcard"
                  }
                  onClick={() => {
                    setFlowType(key);
                    setPage(1);
                  }}
                >
                  <span>{t(`manualPayment.flowCode.${key}`)}</span>
                  <strong>{getFlowLabel(t, key)}</strong>
                </button>
              )
            )}
          </div>

          <AlertBox
            tone="warning"
            message={t("manualPayment.warningSettlement")}
          />
          <AlertBox message={warning} tone="error" />

          <div className="row">
            <div className="col-xl-5 col-12 mb-4">
              <div className="ls-widget h-100">
                <div className="tabs-box">
                  <div className="widget-content p-4">
                    <div className="mpr-panel-title">
                      <h3>{t("manualPayment.searchPanel.title")}</h3>
                      <span>{t("manualPayment.searchPanel.subtitle")}</span>
                    </div>

                    <form onSubmit={handleLookup}>
                      <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                          <label className="mpr-field-label">
                            {t("manualPayment.searchPanel.modeLabel")}
                          </label>
                          <select
                            className="form-control-uld"
                            value={mode}
                            onChange={(event) => setMode(event.target.value)}
                          >
                            <option value="auto">
                              {t("manualPayment.searchPanel.modeAuto")}
                            </option>
                            <option value="bill">
                              {t("manualPayment.searchPanel.modeBill")}
                            </option>
                            <option value="contract">
                              {t("manualPayment.searchPanel.modeContract")}
                            </option>
                          </select>
                        </div>

                        <div className="col-md-8">
                          <label className="mpr-field-label">
                            {t("manualPayment.searchPanel.queryLabel")}
                          </label>
                          <input
                            type="text"
                            className="form-control-uld"
                            placeholder={t("manualPayment.searchPanel.queryPlaceholder")}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                          />
                        </div>

                        <div className="col-12">
                          <button
                            type="submit"
                            className="theme-btn btn-style-one w-100"
                            disabled={busy}
                          >
                            <i className="fas fa-search" />&nbsp; {t("manualPayment.searchPanel.searchButton")}
                          </button>
                        </div>
                      </div>
                    </form>

                    <div className="mpr-recent">
                      <label>
                        {t("manualPayment.searchPanel.pendingOlderThan")}
                        <select
                          value={recentAge}
                          onChange={(event) => setRecentAge(event.target.value)}
                        >
                          <option value="45">
                            {t("manualPayment.searchPanel.age45")}
                          </option>
                          <option value="60">
                            {t("manualPayment.searchPanel.age60")}
                          </option>
                          <option value="180">
                            {t("manualPayment.searchPanel.age180")}
                          </option>
                          <option value="1440">
                            {t("manualPayment.searchPanel.age1440")}
                          </option>
                        </select>
                      </label>

                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        disabled={busy}
                        onClick={findOldPending}
                      >
                        {t("manualPayment.searchPanel.loadPending")}
                      </button>
                    </div>

                    {recentMode && (
                      <p className="mpr-disclaimer">
                        {t("manualPayment.searchPanel.pendingDisclaimer")}
                      </p>
                    )}

                    {candidates.length === 0 ? (
                      <div className="mpr-empty">
                        {t("manualPayment.searchPanel.empty")}
                      </div>
                    ) : (
                      candidates.map((item) => (
                        <div
                          className="mpr-candidate"
                          key={`${item.sourceType}-${item.billNumber}`}
                        >
                          <div className="mpr-candidate-info">
                            <span className="mpr-flow-tag">
                              {getFlowLabel(t, item.flowType)}
                            </span>
                            <strong>{item.billNumber}</strong>
                            <small>
                              {t("manualPayment.searchPanel.contracts", {
                                contracts: (item.contractNumbers || []).join(", ") || "—",
                                count: item.contractCount || 0,
                              })}
                            </small>
                            <small>
                              {t("manualPayment.searchPanel.amountFromDb", {
                                amount: formatNumber(item.expectedAmount),
                                currency: t("manualPayment.currency"),
                              })}
                              {Number(item.expectedAltAmount) !== Number(item.expectedAmount)
                                ? t("manualPayment.searchPanel.altAmount", {
                                  amount: formatNumber(item.expectedAltAmount),
                                })
                                : ""}
                            </small>
                            <small>
                              {t("manualPayment.searchPanel.created", {
                                date: moment.utc(item.createdAt).format('DD-MM-YYYY hh:mm:ss'),
                                source: item.sourceType,
                              })}
                            </small>

                            {(item.details || []).length > 1 && (
                              <details className="mpr-children">
                                <summary>
                                  {t("manualPayment.searchPanel.viewDetails", {
                                    count: item.details.length,
                                  })}
                                </summary>
                                {item.details.map((detail, index) => (
                                  <div key={index}>
                                    {detail.contractNumber || "—"}
                                    {detail.billNumber
                                      ? ` / ${detail.billNumber}`
                                      : ""}
                                    {" · "}
                                    {formatNumber(detail.amount)} {t("manualPayment.currency")}
                                  </div>
                                ))}
                              </details>
                            )}
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            disabled={busy || item.flowType === "UNCLASSIFIED"}
                            onClick={() => openCandidate(item)}
                          >
                            {t("manualPayment.searchPanel.open")}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-7 col-12 mb-4">
              <div className="ls-widget h-100">
                <div className="tabs-box">
                  <div className="widget-content p-4">
                    <div className="mpr-panel-title">
                      <h3>{t("manualPayment.tracking.title")}</h3>
                      <span>{t("manualPayment.tracking.recordCount", { count: paging.totalRecords })}</span>
                    </div>

                    <div className="mpr-filters">
                      <input
                        type="text"
                        className="form-control-uld"
                        placeholder={t("manualPayment.tracking.searchPlaceholder")}
                        value={search}
                        onChange={(event) => {
                          setPage(1);
                          setSearch(event.target.value);
                        }}
                      />

                      <select
                        className="form-control-uld"
                        value={status}
                        onChange={(event) => {
                          setPage(1);
                          setStatus(event.target.value);
                        }}
                      >
                        <option value="">{t("manualPayment.tracking.allStatus")}</option>
                        {STATUS_KEYS.map((key) => (
                          <option key={key} value={key}>
                            {getStatusLabel(t, key)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mpr-tablewrap">
                      <table className="mpr-table">
                        <thead>
                          <tr>
                            <th>{t("manualPayment.tracking.billContract")}</th>
                            <th>{t("manualPayment.tracking.type")}</th>
                            <th>{t("manualPayment.tracking.amount")}</th>
                            <th>{t("manualPayment.tracking.status")}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <strong>{item.billNumber}</strong>
                                <small>
                                  {(item.contractNumbers || []).join(", ") || "—"}
                                </small>
                                <small>{moment.utc(item.createdAt).format('DD-MM-YYYY hh:mm:ss')}</small>
                              </td>
                              <td>
                                {getFlowLabel(t, item.flowType)}
                              </td>
                              <td>{formatNumber(item.expectedAmount)} {t("manualPayment.currency")}</td>
                              <td>
                                <span
                                  className={`mpr-status mpr-status-${String(
                                    item.status || "open"
                                  ).toLowerCase()}`}
                                >
                                  {getStatusLabel(t, item.status)}
                                </span>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="mpr-link"
                                  onClick={() => chooseCase(item.id)}
                                >
                                  {t("manualPayment.tracking.view")}
                                </button>
                              </td>
                            </tr>
                          ))}

                          {!items.length && (
                            <tr>
                              <td colSpan="5" className="mpr-empty">
                                {loading
                                  ? t("manualPayment.tracking.loading")
                                  : t("manualPayment.tracking.noData")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="mpr-pagination">
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPage((value) => value - 1)}
                      >
                        ←
                      </button>
                      <span>
                        {page} / {Math.max(1, paging.totalPages)}
                      </span>
                      <button
                        type="button"
                        disabled={page >= paging.totalPages}
                        onClick={() => setPage((value) => value + 1)}
                      >
                        →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {active && (
            <div className="ls-widget mb-4">
              <div className="tabs-box">
                <div className="widget-content p-4">
                  <div className="mpr-panel-title">
                    <div>
                      <span className="mpr-eyebrow">
                        {t("manualPayment.case.label", { id: active.id })}
                      </span>
                      <h3>{active.billNumber}</h3>
                    </div>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={resetActiveState}
                    >
                      {t("manualPayment.case.close")}
                    </button>
                  </div>

                  <div className="mpr-actions mpr-actions-top">
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm"
                      disabled={busy}
                      onClick={syncPosted}
                    >
                      <i className="fas fa-sync-alt" />&nbsp;
                      {t("manualPayment.case.syncPosted")}
                    </button>
                  </div>

                  <div className="mpr-stats">
                    <div>
                      <span>{t("manualPayment.case.amountInDb")}</span>
                      <strong>{formatNumber(active.expectedAmount)} {t("manualPayment.currency")}</strong>
                      {Number(active.expectedAltAmount) !==
                        Number(active.expectedAmount) && (
                          <small>
                            {t("manualPayment.case.totalAmount", {
                              amount: formatNumber(active.expectedAltAmount),
                              currency: t("manualPayment.currency"),
                            })}
                          </small>
                        )}
                    </div>
                    <div>
                      <span>{t("manualPayment.case.paymentFlow")}</span>
                      <strong>
                        {getFlowLabel(t, active.flowType)}
                      </strong>
                    </div>
                    <div>
                      <span>{t("manualPayment.case.reviewStatus")}</span>
                      <strong>{getStatusLabel(t, active.status)}</strong>
                    </div>
                  </div>

                  <div className="mpr-contract-summary">
                    <strong>{t("manualPayment.case.linkedContracts")}</strong>
                    <div>
                      {(active.contractNumbers || []).map((contract) => (
                        <span className="mpr-contract-pill" key={contract}>
                          {contract}
                        </span>
                      ))}
                      {!(active.contractNumbers || []).length &&
                        t("manualPayment.case.noContract")}
                    </div>
                    <small>
                      {t("manualPayment.case.groupedBillNote")}
                    </small>
                  </div>

                  {!!(active.lineItems || []).length && (
                    <div className="mpr-line-items">
                      <h4>
                        {t("manualPayment.case.lineItemsTitle", {
                          count: active.lineItems.length,
                        })}
                      </h4>
                      <div className="mpr-tablewrap">
                        <table className="mpr-table">
                          <thead>
                            <tr>
                              <th>{t("manualPayment.case.contract")}</th>
                              <th>{t("manualPayment.case.childBillInstallment")}</th>
                              <th>{t("manualPayment.case.lineAmount")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {active.lineItems.map((item, index) => (
                              <tr key={index}>
                                <td>{item.contractNumber || "—"}</td>
                                <td>
                                  {item.billNumber ||
                                    (item.paidMonth && item.paidYear
                                      ? `${item.paidMonth}/${item.paidYear}`
                                      : "—")}
                                </td>
                                <td>{formatNumber(item.amount)} {t("manualPayment.currency")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {["OPEN", "REJECTED"].includes(active.status) && (
                    <form className="mpr-form" onSubmit={handleSubmitProof}>
                      <h4>{t("manualPayment.proofForm.title")}</h4>
                      <div className="mpr-formgrid">
                        <label>
                          {t("manualPayment.proofForm.reference")}
                          <input
                            required
                            maxLength="120"
                            value={form.paymentReference}
                            placeholder={t("manualPayment.proofForm.referencePlaceholder")}
                            onChange={(event) =>
                              setForm((value) => ({
                                ...value,
                                paymentReference: event.target.value,
                              }))
                            }
                          />
                        </label>

                        <label>
                          {t("manualPayment.proofForm.amount")}
                          <input
                            required
                            type="number"
                            inputMode="decimal"
                            min="0.01"
                            step="0.01"
                            value={form.paidAmount}
                            onChange={(event) =>
                              setForm((value) => ({
                                ...value,
                                paidAmount: event.target.value,
                              }))
                            }
                          />
                        </label>

                        <label>
                          {t("manualPayment.proofForm.paidAt")}
                          <input
                            required
                            type="datetime-local"
                            value={form.paidAt}
                            onChange={(event) =>
                              setForm((value) => ({
                                ...value,
                                paidAt: event.target.value,
                              }))
                            }
                          />
                          {/* <DatePicker
                            selected={form.paidAt}
                            onChange={(date) => setForm((value) => ({
                              ...value,
                              paidAt: date,
                            }))}
                            dateFormat="dd/MM/yyyy"
                            placeholderText={t('manualPayment.proofForm.paidAt')}
                            className="form-control-uld"
                            required
                          /> */}
                        </label>

                        <label>
                          {t("manualPayment.proofForm.slip")}
                          <input
                            required
                            type="file"
                            accept="image/jpeg,image/png,image/webp,application/pdf"
                            onChange={(event) =>
                              setForm((value) => ({
                                ...value,
                                slip: event.target.files?.[0] || null,
                              }))
                            }
                          />
                        </label>
                      </div>

                      <label>
                        {t("manualPayment.proofForm.note")}
                        <textarea
                          rows="2"
                          maxLength="1000"
                          value={form.note}
                          onChange={(event) =>
                            setForm((value) => ({
                              ...value,
                              note: event.target.value,
                            }))
                          }
                        />
                      </label>

                      <button
                        type="submit"
                        className="theme-btn btn-style-one"
                        disabled={busy}
                      >
                        {t("manualPayment.proofForm.submit")}
                      </button>
                    </form>
                  )}

                  {Boolean(active.amountMismatch) && (
                    <AlertBox
                      tone="error"
                      message={t("manualPayment.alerts.amountMismatch")}
                    />
                  )}

                  {active.hasProof && (
                    <div className="mpr-evidence">
                      <div>
                        <strong>
                          {t("manualPayment.evidence.reference", {
                            reference: active.paymentReference,
                          })}
                        </strong>
                        <p>
                          {t("manualPayment.evidence.paid", {
                            amount: formatNumber(active.paidAmount),
                            currency: t("manualPayment.currency"),
                            date: moment(active.paidAt).format('DD-MM-YYYY hh:mm:ss'),
                          })}
                        </p>
                        <small>
                          {t("manualPayment.evidence.hash", { hash: active.proofSha256 })}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        disabled={busy}
                        onClick={showProof}
                      >
                        {t("manualPayment.evidence.viewSlip")}
                      </button>
                    </div>
                  )}

                  {active.status === "SUBMITTED" &&
                    active.permissions?.canReview && (
                      <div className="mpr-form">
                        <h4>{t("manualPayment.review.title")}</h4>
                        <p>{t("manualPayment.review.description")}</p>
                        <textarea
                          rows="3"
                          maxLength="1000"
                          value={decisionNote}
                          placeholder={t("manualPayment.review.placeholder")}
                          onChange={(event) => setDecisionNote(event.target.value)}
                        />
                        <div className="mpr-actions">
                          <button
                            type="button"
                            className="btn btn-light"
                            disabled={busy}
                            onClick={() => reviewCase("reject")}
                          >
                            {t("manualPayment.review.reject")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-success"
                            disabled={busy}
                            onClick={() => reviewCase("approve")}
                          >
                            {t("manualPayment.review.approve")}
                          </button>
                        </div>
                      </div>
                    )}

                  {active.status === "MANUAL_VERIFIED" &&
                    active.permissions?.canPost && (
                      <div className="mpr-finalize">
                        <div className="mpr-finalize-head">
                          <div>
                            <span className="mpr-eyebrow">
                              {t("manualPayment.finalize.eyebrow")}
                            </span>
                            <h4>{t("manualPayment.finalize.title")}</h4>
                            <p>
                              {t("manualPayment.finalize.description")}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            disabled={busy}
                            onClick={reloadPreflight}
                          >
                            {t("manualPayment.finalize.reloadPreflight")}
                          </button>
                        </div>

                        <div
                          className={`mpr-readiness ${preflight?.ready ? "mpr-ready" : "mpr-blocked"
                            }`}
                        >
                          <strong>
                            {preflight?.ready
                              ? t("manualPayment.finalize.ready")
                              : t("manualPayment.finalize.blocked")}
                          </strong>
                          <small>
                            {t("manualPayment.finalize.billQrAmount", {
                              billNumber: active.billNumber,
                              amount:
                                preflight?.qrAmount != null
                                  ? `${formatNumber(preflight.qrAmount)} ${t("manualPayment.currency")}`
                                  : t("manualPayment.finalize.notFound"),
                            })}
                          </small>
                        </div>

                        {!!preflight?.writePlan?.length && (
                          <div className="mpr-writeplan">
                            <strong>{t("manualPayment.finalize.writePlanTitle")}</strong>
                            <div>
                              {preflight.writePlan.map((part, index) => (
                                <span key={index}>{part}</span>
                              ))}
                            </div>
                            <small>{t("manualPayment.finalize.writePlanNote")}</small>
                          </div>
                        )}

                        {!!preflight?.issues?.length && (
                          <div className="mpr-readiness-issues">
                            {preflight.issues.map((issue, index) => (
                              <p key={index}>• {issue}</p>
                            ))}
                          </div>
                        )}

                        <label className="mpr-attest">
                          <input
                            type="checkbox"
                            checked={confirmFunds}
                            onChange={(event) => setConfirmFunds(event.target.checked)}
                          />
                          <span>{t("manualPayment.finalize.attestation")}</span>
                        </label>

                        <label className="mpr-final-note">
                          {t("manualPayment.finalize.noteLabel")}
                          <textarea
                            rows="3"
                            maxLength="1000"
                            value={finalNote}
                            placeholder={t("manualPayment.finalize.notePlaceholder")}
                            onChange={(event) => setFinalNote(event.target.value)}
                          />
                        </label>

                        <button
                          type="button"
                          className="btn btn-success w-100"
                          disabled={
                            busy ||
                            !preflight?.ready ||
                            !confirmFunds ||
                            finalNote.trim().length < 12
                          }
                          onClick={bookPayment}
                        >
                          {t("manualPayment.finalize.button")}
                        </button>
                      </div>
                    )}

                  {active.status === "MANUAL_VERIFIED" &&
                    !active.permissions?.canPost && (
                      <AlertBox
                        tone="warning"
                        message={t("manualPayment.alerts.waitingPostPermission")}
                      />
                    )}

                  {active.status === "ALREADY_POSTED" && (
                    <div className="mpr-posted-note">
                      <strong>
                        {t("manualPayment.finalize.posted")}
                      </strong>
                      <small>
                        {t("manualPayment.finalize.source", {
                          source: getFinalizationSourceLabel(active.finalizationSource),
                        })}
                      </small>
                    </div>
                  )}

                  {proofUrl && (
                    <div className="mpr-proof">
                      <div className="mpr-actions mpr-actions-between">
                        <strong>{t("manualPayment.proof.title")}</strong>
                        <button
                          type="button"
                          className="btn btn-light btn-sm"
                          onClick={closeProof}
                        >
                          {t("manualPayment.proof.closeImage")}
                        </button>
                      </div>

                      {proofMime === "application/pdf" ? (
                        <a href={proofUrl} download={`payment-proof-${active.id}.pdf`}>
                          {t("manualPayment.proof.downloadPdf")}
                        </a>
                      ) : (
                        <img src={proofUrl} alt={t("manualPayment.proof.imageAlt")} />
                      )}
                    </div>
                  )}

                  <div className="mpr-timeline">
                    <h4>{t("manualPayment.timeline.title")}</h4>
                    {(active.events || []).map((event, index) => (
                      <div key={index} className="mpr-event">
                        <span className="mpr-dot" />
                        <div>
                          <strong>
                            {event.action} · {event.actorId}
                          </strong>
                          <p>{event.note || "—"}</p>
                          <small>{moment(event.happenedAt).format('DD-MM-YYYY hh:mm:ss')}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <CopyrightFooter />
    </div>
  );
};

export default ManualPaymentDesk;
